import dayjs from 'dayjs';
import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  FirestoreError,
  onSnapshot,
  orderBy,
  query,
  Query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import {
  Associate,
  CreateVisitData,
  SiteVisit,
  Route,
  RoutePlan,
  Visit,
  CreateSiteVisitData,
} from '../../../../global';
import { addSiteVisit } from '../db/siteVisits';
import { addVisit } from '../db/visits';
import { firestore, docToJSON } from '../utils/firebase';
import { usePagination } from '../utils/usePagination';
import { useAuth } from './use-auth';

type VisitsHook = {
  error: FirebaseError;
  hasMore: boolean;
  load: () => void;
  loading: boolean;
  reset: () => void;
  visits: SiteVisit[];
};

type VisitsHookOptions = {
  month: number;
  year: number;
  day: number;
};

export const useSiteVisits = ({
  month,
  year,
  day,
}: VisitsHookOptions): VisitsHook => {
  const { id } = useAuth();
  const [visitsQuery, setVisitsQuery] = useState<Query>();
  const { error, hasMore, load, loading, reset, values } = usePagination(
    visitsQuery,
    0
  );

  useEffect(() => {
    if (id) {
      setVisitsQuery(
        query(
          collection(firestore, 'siteVisits'),
          where('createdBy.id', '==', id),
          where('day', '==', day),
          where('month', '==', month),
          where('year', '==', year)
        )
      );
    } else if (visitsQuery) {
      setVisitsQuery(undefined);
    }
  }, [id, month, year, day]);

  useEffect(() => {
    console.log(error);
  }, [error]);

  return useMemo(
    () => ({
      error,
      hasMore,
      load,
      loading,
      reset,
      visits: values
        ? values.map((v) => docToJSON(v) as SiteVisit)
        : values,
    }),
    [error, hasMore, load, loading, reset, values]
  );
};

export const useSiteVisit = (id?: string) => {
  const [loading, setLoading] = useState(false);
  const [visit, setVisit] = useState<SiteVisit | undefined>();
  const [error, setError] = useState<FirestoreError>();

  useEffect(() => {
    let unsubscribe;
    if (id) {
      setLoading(true);
      const ref = doc(firestore, 'siteVisits', id);
      unsubscribe = onSnapshot(
        ref,
        (doc) => {
          if (doc.exists()) {
            setVisit(docToJSON(doc) as SiteVisit);
          } else {
            setVisit(undefined);
          }
          setLoading(false);
        },
        (error: FirestoreError) => {
          setLoading(false);
          setError(error);
        }
      );
    } else {
      setVisit(undefined);
    }
    return unsubscribe;
  }, [id]);

  /*
  const update = async (data: any) => {
    if (id) {
      try {
        setLoading(true);
        await firestore.collection('visits').doc(id).update(data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);
        setError(err.message);
      }
    }
  };
*/

  const add = async (data: CreateSiteVisitData) => {
    setLoading(true);
    await addSiteVisit(data);
    setLoading(false);
  };

  useEffect(() => {
    console.log(error);
  }, [error]);

  return {
    visit,
    error,
    loading,
    //update,
    add,
  };
};

const prepareVisit = (visit: SiteVisit): SiteVisit => {
  return {
    ...visit,
  };
};
